/* global SWDLocalise */

const getAutofillMap = (input) => {
    try {
        return JSON.parse(input.dataset.autofillMap || '{}');
    } catch (error) {
        return {};
    }
};

const resolveTargetKey = (baseKey, batchIndex) => {
    if (!baseKey) return '';

    return batchIndex > 0 ? `${baseKey}__${batchIndex}` : baseKey;
};

const getLookupScope = (input) => {
    const batch = input.closest('.js-form-field-group-batch');

    if (batch) {
        return batch;
    }

    const form = input.closest('.js-form');

    if (!form) {
        return document;
    }

    const { autofillScope } = input.dataset;

    if (autofillScope) {
        const scopedInputs = form.querySelectorAll(`.js-form-input[data-autofill-scope="${autofillScope}"]`);

        if (scopedInputs.length > 0) {
            return scopedInputs;
        }
    }

    return form;
};

const findTargetInput = (scope, targetKey) => {
    if (scope instanceof NodeList || Array.isArray(scope)) {
        return [...scope].find((element) => {
            const baseKey = element.dataset.baseKey || element.dataset.key.replace(/__\d+$/, '');

            return element.dataset.key === targetKey || baseKey === targetKey;
        }) || null;
    }

    const exactMatch = scope.querySelector?.(`.js-form-input[data-key="${targetKey}"]`);

    if (exactMatch) {
        return exactMatch;
    }

    return [...scope.querySelectorAll('.js-form-input')].find((element) => {
        const baseKey = element.dataset.baseKey || element.dataset.key.replace(/__\d+$/, '');

        return element.dataset.key === targetKey || baseKey === targetKey;
    }) || null;
};

export const setStatus = (input, message, type = 'neutral') => {
    const status = input.parentElement?.querySelector('.js-form-company-number-status');

    if (!status) return;

    status.textContent = message;
    status.classList.remove('hidden', 'bg-error', 'bg-success', 'bg-black/60');

    switch (type) {
        case 'error':
            status.classList.add('bg-error');
            break;
        case 'success':
            status.classList.add('bg-success');
            break;
        default:
            status.classList.add('bg-black/60');
            break;
    }
};

const fillCompanyFields = (input, data) => {
    const map = getAutofillMap(input);

    if (!Object.keys(map).length) {
        return;
    }

    const batch = input.closest('.js-form-field-group-batch');
    const batchIndex = batch ? Number(batch.dataset.batchIndex || 0) : 0;
    const scope = getLookupScope(input);

    Object.entries(map).forEach(([apiKey, targetBaseKey]) => {
        if (!data[apiKey]) {
            return;
        }

        const targetKey = resolveTargetKey(targetBaseKey, batchIndex);
        const target = findTargetInput(scope, targetKey);

        if (target && target !== input) {
            target.value = data[apiKey];
            target.dispatchEvent(new Event('input', { bubbles: true }));
        }
    });
};

export const lookupCompany = async (input) => {
    const companyNumber = input.value.trim();

    if (!companyNumber) {
        setStatus(input, 'Please enter a company number.');
        return;
    }

    if (!Object.keys(getAutofillMap(input)).length) {
        setStatus(input, 'Autofill targets are not configured for this field.', 'error');
        return;
    }

    setStatus(input, 'Looking up company…');

    const payload = new FormData();
    payload.append('action', 'swd_companies_house_lookup');
    payload.append('nonce', SWDLocalise.companiesHouseNonce);
    payload.append('company_number', companyNumber);

    try {
        const response = await fetch(SWDLocalise.ajaxUrl, {
            method: 'POST',
            cache: 'no-cache',
            body: payload,
        });

        const result = await response.json();

        if (!result.success) {
            setStatus(input, result.message || 'Unable to look up that company number.', 'error');
            return;
        }

        fillCompanyFields(input, result.data);
        setStatus(input, 'Company details found.', 'success');
    } catch (error) {
        setStatus(input, 'Unable to look up that company number.', 'error');
    }
};

export const resetCompanyNumberStatuses = (root) => {
    root.querySelectorAll('.js-form-company-number').forEach((input) => {
        const status = input.parentElement?.querySelector('.js-form-company-number-status');

        if (!status) return;

        status.textContent = '';
        status.classList.add('hidden');
        status.classList.remove('bg-error', 'bg-success', 'bg-black/60');
    });
};

const initCompanyNumberInputs = (root) => {
    root.querySelectorAll('.js-form-company-number').forEach((input) => {
        if (input.dataset.companyLookupInit) return;

        input.dataset.companyLookupInit = 'true';

        input.addEventListener('blur', () => {
            lookupCompany(input);
        });
    });
};

export default initCompanyNumberInputs;
